// ENG-2277 (HTML Forms): "Fit on one page". Deterministic, no AI call: tighten paragraph spacing
// first, then lower text size half a point at a time, never below 7pt, re-counting the pages after
// each step (tech plan D15).
import type { FormSettings } from "components/pdf-generation-v3/form-print.utils";

export const MAX_FIT_ITERATIONS = 6;

const MIN_FONT_PT = 7;
const FONT_STEP_PT = 0.5;
// The PDF's default space above a paragraph and text size (DEFAULT_PDF_CSS_STYLE). A Form prints no
// space below a paragraph (FORM_PDF_CSS_STYLE), so only the space above is ever tightened.
const DEFAULT_PARAGRAPH_SPACING_PX = 10;
const DEFAULT_FONT_PT = 10.5;
const SPACING_STEPS_PX = [6, 2];
// Each spacing step also halves the form's spacers (the converter's blocks of empty space).
const SPACER_STEP = 0.5;

const PX_PER_UNIT: Record<string, number> = { px: 1, pt: 96 / 72, in: 96 };

const lengthPx = (value: string) => {
  const match = /^([\d.]+)(px|pt|in)$/.exec(value.trim());
  return match ? parseFloat(match[1]) * PX_PER_UNIT[match[2]] : null;
};

const ptValue = (value: string) => {
  const px = lengthPx(value);
  return px === null || /in$/.test(value.trim()) ? null : px * 0.75;
};

const roundPt = (value: number) => Math.round(value * 100) / 100;

/** The form's spacers: the converter's blocks of empty space (render-form-document.util). */
export const spacersOf = (body: HTMLElement) =>
  Array.from(body.querySelectorAll<HTMLElement>("div")).filter(
    (div) =>
      div.style.height &&
      div.style.overflow === "hidden" &&
      div.children.length === 0 &&
      !(div.textContent ?? "").trim()
  );

const halve = (length: string) => {
  const match = /^([\d.]+)(px|pt|in)$/.exec(length.trim());
  return match
    ? `${Math.round(parseFloat(match[1]) * SPACER_STEP * 1000) / 1000}${match[2]}`
    : length;
};

const tightenSpacing = (body: HTMLElement) => {
  const paragraphs = Array.from(body.querySelectorAll<HTMLElement>("p"));
  const spaceAbove = (paragraph: HTMLElement) =>
    paragraph.style.marginTop
      ? lengthPx(paragraph.style.marginTop)
      : DEFAULT_PARAGRAPH_SPACING_PX;
  const widest = Math.max(0, ...paragraphs.map((p) => spaceAbove(p) ?? 0));
  const target = SPACING_STEPS_PX.find((step) => widest > step);
  if (target === undefined) return false;
  for (const paragraph of paragraphs) {
    const current = spaceAbove(paragraph);
    if (current !== null && current > target)
      paragraph.style.marginTop = `${target}px`;
    paragraph.style.marginBottom = "0px";
  }
  for (const spacer of spacersOf(body))
    spacer.style.height = halve(spacer.style.height);
  return true;
};

const lowerTextSize = (body: HTMLElement) => {
  let changed = false;
  const lower = (element: HTMLElement, sizePt: number) => {
    const next = Math.max(MIN_FONT_PT, roundPt(sizePt - FONT_STEP_PT));
    if (next < sizePt) {
      element.style.fontSize = `${next}pt`;
      changed = true;
    }
  };
  for (const element of Array.from(
    body.querySelectorAll<HTMLElement>("[style*='font-size']")
  )) {
    const size = ptValue(element.style.fontSize);
    if (size !== null) lower(element, size);
  }
  // Text that sets no size of its own prints at the default size.
  for (const element of Array.from(body.children) as HTMLElement[]) {
    if (!element.style.fontSize) lower(element, DEFAULT_FONT_PT);
  }
  return changed;
};

/** The form one step smaller, or null when it cannot shrink any further. */
export const shrinkStep = (html: string): string | null => {
  const { body } = new DOMParser().parseFromString(html, "text/html");
  const changed = tightenSpacing(body) || lowerTextSize(body);
  return changed ? body.innerHTML : null;
};

export type FitResult = { fitted: boolean; html: string };

/**
 * Shrinks until the form prints on one page, within MAX_FIT_ITERATIONS steps. A form that cannot
 * fit comes back unchanged, so nothing is half-applied.
 */
export const fitOnOnePage = async (
  html: string,
  formSettings: FormSettings | null,
  countPages: (
    html: string,
    formSettings: FormSettings | null
  ) => Promise<number>
): Promise<FitResult> => {
  if ((await countPages(html, formSettings)) <= 1)
    return { fitted: true, html };
  let current = html;
  for (let step = 0; step < MAX_FIT_ITERATIONS; step++) {
    const next = shrinkStep(current);
    if (next === null) break;
    current = next;
    if ((await countPages(current, formSettings)) <= 1)
      return { fitted: true, html: current };
  }
  return { fitted: false, html };
};
